import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';

import { Box, styled } from '@mui/system';
import {
  Card,
  Stack,
  Alert,
  useTheme,
  TextField,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
  InputAdornment
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { useGetBoardByOrganizationQuery } from 'src/services/jobs/jobs.service';

import Iconify from 'src/components/iconify';
import FeatureLockedButton from 'src/components/billing/feature-locked-button';
import { CopyButton, CodeContent, CodeContainer } from 'src/components/code/code-container';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import CareersUrlSetting from 'src/sections/admin/organization/settings/components/careers-url-setting';

import { FeatureType } from 'src/types/subscription.types';

const StyledTextField = styled(TextField)(({ theme }: { theme: any }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
  },
}));

export default function JobBoardCard() {

  const { t } = useTranslate();

  const theme = useTheme();

  const { canAccess } = useFeatures();

  const authContext = useAuthContext();

  const { currentData: jobBoard, isLoading: retrievingJobBoard } = useGetBoardByOrganizationQuery(
    {
      organizationId: authContext.organization.id,
    },
    {
      skip: !authContext.organization.id,
    }
  );

  const handleCopy = async (text: string) => {
    if (!navigator.clipboard || !text) return;
    navigator.clipboard.writeText(text);
    enqueueSnackbar(t('common.copied_to_clipboard'), { variant: 'success' });
  };

  const jobBoardFeatureEnabled = useMemo(() => canAccess(FeatureType.JOB_BOARD_ENABLED), [canAccess]);

  if (retrievingJobBoard || !jobBoard) {
    return (
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography>{t('common.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="proicons:board"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.job-board.title')}
            </Typography>
          }
        />

        <CardContent>
          <Stack spacing={3}>

            <Box>
              {
                !jobBoardFeatureEnabled &&
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  action={
                    <FeatureLockedButton
                      variant="text"
                      label={t('common.upgrade')}
                      size="small"
                      sx={{ color: 'inherit', textDecoration: 'underline' }}
                    />
                  }
                >
                  {t('admin-organization.job-board.feature_disabled_alert')}
                </Alert>
              }
            </Box>

            {/* Embed Code Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {t('admin-organization.job-board.embed-code')}
              </Typography>
              <CodeContainer elevation={2}>
                <CodeContent>{jobBoard.embed_code}</CodeContent>
                <CopyButton
                  size="small"
                  onClick={() => handleCopy(jobBoard.embed_code)}
                  title={t('common.copy_link')}
                >
                  <Iconify icon="material-symbols:content-copy" />
                </CopyButton>
              </CodeContainer>
            </Box>

            {/* Careers page on the organization's own site */}
            <CareersUrlSetting />

            {/* Public Link Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {t('admin-organization.job-board.public-link')}
              </Typography>
              <StyledTextField
                fullWidth
                value={jobBoard.link}
                disabled
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={1}>
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            boxShadow: theme.shadows[2],
                          }}
                          size="small"
                          onClick={() => window.open(jobBoard.link, '_blank')}
                          title={t('common.copy_link')}
                        >
                          <Iconify icon="material-symbols:open-in-new" />
                        </IconButton>
                        <IconButton
                          sx={{
                            backgroundColor: theme.palette.background.paper,
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            boxShadow: theme.shadows[2],
                          }}
                          size="small"
                          onClick={() => handleCopy(jobBoard.link)}
                          title={t('common.copy_link')}
                        >
                          <Iconify icon="material-symbols:content-copy" />
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}